import * as React from 'react';
import { jobsApi } from '../services/jobsApi';
import type { JobRunStatus, JobRunSummary, JobRunsListResponse } from '../types/jobs';

const ACTIVE_STATUSES = new Set<JobRunStatus>(['pending', 'running']);
const POLL_INTERVAL_MS = 5_000;

interface UseJobRunsOptions {
  page?: number;
  pageSize?: number;
  status?: JobRunStatus;
}

export function useJobRuns(projectUuid: string, options: UseJobRunsOptions = {}) {
  const { page = 1, pageSize = 20, status } = options;

  const [data, setData] = React.useState<JobRunsListResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const hasActiveRun = React.useMemo<boolean>(
    () => data?.jobs.some((j: JobRunSummary) => ACTIVE_STATUSES.has(j.status)) ?? false,
    [data]
  );

  const load = React.useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const result = await jobsApi.listJobRuns(projectUuid, page, pageSize, status);
        setData(result);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [projectUuid, page, pageSize, status]
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    if (hasActiveRun) {
      intervalRef.current = setInterval(() => void load(true), POLL_INTERVAL_MS);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasActiveRun, load]);

  return { data, loading, error, refresh: load };
}
